import type { PaymentMethod } from '@/features/payments/domain/model/payment-method';

/** Estados del flujo de cobro del take order (dashboard y carta web). */
export type CheckoutStep =
  | 'select_method'
  | 'creating_order'
  | 'awaiting_customer_payment'
  | 'awaiting_terminal'
  | 'redirecting_to_mp'
  | 'closing_order'
  | 'cashier_confirmation'
  | 'payment_error';

export type CheckoutQrMode = 'static' | 'dynamic' | 'hybrid';

export type CheckoutFlowState = {
  step: CheckoutStep;
  /** Orden creada en este intento de cobro; sobrevive a "atrás" para poder cancelarla al cerrar. */
  orderId: string | null;
  methodType: PaymentMethod['type'] | null;
  qrMode: CheckoutQrMode;
  qrData: string | null;
  /** Código de caja del pedido (solo tótem público con efectivo). */
  cashNumber: string | null;
  errorMessage: string | null;
  /** `true` cuando el error admite reintentar el cierre con el mismo `orderId`. */
  canRetryClose: boolean;
};

export const initialCheckoutFlowState: CheckoutFlowState = {
  step: 'select_method',
  orderId: null,
  methodType: null,
  qrMode: 'dynamic',
  qrData: null,
  cashNumber: null,
  errorMessage: null,
  canRetryClose: false,
};

export type CheckoutFlowAction =
  | { type: 'start'; methodType: PaymentMethod['type'] | null }
  | { type: 'order_created'; orderId: string }
  | { type: 'await_qr'; qrMode: CheckoutQrMode; qrData: string | null }
  | { type: 'await_terminal' }
  | { type: 'redirect_to_mp' }
  | { type: 'close_order' }
  | { type: 'cashier_confirmation'; cashNumber: string | null }
  | { type: 'fail'; message: string; canRetryClose?: boolean }
  | { type: 'back' }
  | { type: 'reset' };

/** Transiciones válidas. Una transición no listada deja el estado intacto (se testea). */
const ALLOWED_TRANSITIONS: Record<CheckoutStep, CheckoutStep[]> = {
  select_method: ['creating_order'],
  creating_order: [
    'creating_order',
    'awaiting_customer_payment',
    'awaiting_terminal',
    'redirecting_to_mp',
    'closing_order',
    'cashier_confirmation',
    'payment_error',
    'select_method',
  ],
  awaiting_customer_payment: ['closing_order', 'cashier_confirmation', 'payment_error', 'select_method'],
  awaiting_terminal: ['closing_order', 'payment_error', 'select_method'],
  redirecting_to_mp: ['payment_error', 'select_method'],
  closing_order: ['cashier_confirmation', 'payment_error', 'select_method'],
  cashier_confirmation: ['select_method'],
  payment_error: ['creating_order', 'closing_order', 'select_method'],
};

function isAllowed(from: CheckoutStep, to: CheckoutStep): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

function transition(
  state: CheckoutFlowState,
  step: CheckoutStep,
  patch: Partial<CheckoutFlowState> = {}
): CheckoutFlowState {
  if (!isAllowed(state.step, step)) return state;
  return { ...state, ...patch, step };
}

export function checkoutFlowReducer(state: CheckoutFlowState, action: CheckoutFlowAction): CheckoutFlowState {
  switch (action.type) {
    case 'start':
      return transition(state, 'creating_order', {
        methodType: action.methodType,
        orderId: null,
        qrMode: 'dynamic',
        qrData: null,
        cashNumber: null,
        errorMessage: null,
        canRetryClose: false,
      });

    case 'order_created':
      if (state.step !== 'creating_order') return state;
      return { ...state, orderId: action.orderId };

    case 'await_qr':
      return transition(state, 'awaiting_customer_payment', { qrMode: action.qrMode, qrData: action.qrData });

    case 'await_terminal':
      return transition(state, 'awaiting_terminal');

    case 'redirect_to_mp':
      return transition(state, 'redirecting_to_mp');

    case 'close_order':
      return transition(state, 'closing_order', { errorMessage: null, canRetryClose: false });

    case 'cashier_confirmation':
      return transition(state, 'cashier_confirmation', { cashNumber: action.cashNumber });

    case 'fail':
      return transition(state, 'payment_error', {
        errorMessage: action.message,
        canRetryClose: action.canRetryClose ?? false,
      });

    case 'back':
      // Vuelve al inicio conservando el `orderId`: si el cajero cierra el modal, se cancela.
      return transition(state, 'select_method', {
        qrData: null,
        qrMode: 'dynamic',
        cashNumber: null,
        errorMessage: null,
        canRetryClose: false,
      });

    case 'reset':
      return initialCheckoutFlowState;

    default:
      return state;
  }
}
